/**
 * SoleadLab 2.0 — IV Device Adapter Registry
 *
 * Each adapter describes a known IV measurement device's file format.
 * Adapters pre-fill the import wizard (block config or column mapping)
 * so users don't need to manually map fields every time.
 *
 * Adding a new device: implement IVDeviceAdapter and push to IV_ADAPTERS.
 */

import type { BlockMapping, BlockCellRef, ColumnMapping, LabelMatchingConfig } from './ivParser';

// ─── Interface ────────────────────────────────────────────────────────────────

export interface IVDeviceAdapter {
  id: string;
  name: string;
  manufacturer: string;
  description: string;
  icon: string;             // emoji icon for the device card
  mode: 'block' | 'column' | 'transposed';

  /** Block mode: each measurement spans multiple rows */
  blockConfig?: {
    blockSize: number;      // rows per block (including separator)
    firstRow: number;       // 0-indexed first block start
    cellMapping: BlockMapping;
  };

  /** Column / transposed mode */
  columnConfig?: {
    headerRowIndex: number;
    transposed?: boolean;
    columnMapping: Partial<ColumnMapping>;
  };

  /**
   * Additional per-measurement fields to extract from each block.
   * Keys become the extras map keys stored on the measurement.
   * The special key "result" is also parsed for a deviation percentage.
   */
  extrasMapping?: Record<string, BlockCellRef>;

  /**
   * Session-level metadata extracted from the FIRST block only.
   * (manufacturer, model, etc. — same value across all blocks)
   * Stored as file_metadata on the session after import.
   */
  sessionFields?: Array<{ key: string; label: string; cell: BlockCellRef }>;

  /**
   * Label-based matching config (takes priority over blockConfig when present).
   * Finds values by searching for label text in each block row — works across
   * all sheet variants (min/mid/full) without hardcoded row positions.
   */
  labelMapping?: LabelMatchingConfig;

  /** Returns confidence 0–1. 0.8+ = strong match, shown as "detected". */
  detect(rawRows: unknown[][], fileName: string): number;

  /**
   * Optional: returns true if this sheet is structurally valid to import.
   * Used to dim/exclude sheets with a different format (e.g. "samples").
   * If not defined, all sheets are included.
   */
  filterSheet?(rawRows: unknown[][], sheetName: string): boolean;

  /**
   * Optional: returns true if this sheet should be pre-selected by default.
   * Use when only a subset of valid sheets is recommended (e.g. only "full").
   * Falls back to filterSheet result when not defined.
   */
  recommendSheet?(sheetName: string): boolean;

  /**
   * Optional: accurate measurement count for a sheet.
   * If not defined, falls back to Math.floor(rows / blockSize).
   */
  estimateCount?(rawRows: unknown[][]): number;
}

// ─── Known Device Adapters ────────────────────────────────────────────────────

/**
 * HT Italia IV600 — "full" sheet (16-row blocks)
 *
 * Sheet layout per block (0-indexed rows, cols A=0 B=1 C=2 D=3 E=4 F=5 G=6):
 *   R0:  Manufacturer(B)  | Num modules(D)   | Test number(F)
 *   R1:  Model(B)         | Num strings(D)   | Date and time(F)
 *   R2:  Module Type(B)   | Age of service(D)
 *   R3:  Degradation%(B)  | Irr front(D)     | Marker 1(F)
 *   R4:  Pmax Tol+(B)     | Irr back(D)      | Marker 2(F)
 *   R5:  Pmax Tol-(B)     | Temperature(D)   | Marker 3(F)
 *   R6:  Alpha(B)         | Irr mode(D)
 *   R7:  Beta(B)          | Temp mode(D)
 *   R8:  Rs(B)            | Ref cell temp(D)
 *   R9:  FF@Nominal(B)    | FF@OPC(D)        | FF@STC(F)
 *   R10: Voc@Nominal(B)   | Voc@OPC(D)       | Voc@STC(F)
 *   R11: Vmpp@Nominal(B)  | Vmpp@OPC(D)      | Vmpp@STC(F)
 *   R12: Impp@Nominal(B)  | Impp@OPC(D)      | Impp@STC(F)
 *   R13: Isc@Nominal(B)   | Isc@OPC(D)       | Isc@STC(F)
 *   R14: Pmax@Nominal(B)  | Pmax@OPC(D)      | Pmax@STC(F)  | Result(G)
 *   R15: (empty separator)
 */
const HT_IV600: IVDeviceAdapter = {
  id: 'ht-iv600',
  name: 'IV600',
  manufacturer: 'HT Italia',
  description: 'String/module IV tester — use the "full" sheet for complete data (16-row blocks)',
  icon: '📊',
  mode: 'block',
  blockConfig: {
    blockSize: 16,   // 15 data rows + 1 empty separator
    firstRow: 0,
    cellMapping: {
      // Primary label: Marker 1 (user-assigned module/string identifier)
      label:       { row: 3,  col: 5 },  // Marker 1 (F4)
      // STC-normalized electrical values
      voltage_oc:  { row: 10, col: 5 },  // Voc@STC  [V]
      voltage_mpp: { row: 11, col: 5 },  // Vmpp@STC [V]
      current_mpp: { row: 12, col: 5 },  // Impp@STC [A]
      current_sc:  { row: 13, col: 5 },  // Isc@STC  [A]
      power_mpp:   { row: 14, col: 5 },  // Pmax@STC [W]
      fill_factor: { row: 9,  col: 5 },  // FF@STC
      // Conditions
      irradiance:  { row: 3,  col: 3 },  // Irradiance front [W/m²]
      module_temp: { row: 5,  col: 3 },  // Temperature [°C]
    },
  },

  // Label-based matching — works across min/mid/full sheets automatically.
  // The parser searches each block for the label text (e.g. "Pmax@STC [W]")
  // in cols A/C/E and reads the value from the cell immediately to its right.
  labelMapping: {
    fieldLabels: {
      label:       ['Marker 1', 'Label / ID', 'Label/ID'],
      marker2:     ['Marker 2'],
      marker3:     ['Marker 3'],
      voltage_oc:  ['Voc@STC', 'Voc@STC [V]'],
      voltage_mpp: ['Vmpp@STC', 'Vmpp@STC [V]'],
      current_mpp: ['Impp@STC', 'Impp@STC [A]'],
      current_sc:  ['Isc@STC', 'Isc@STC [A]'],
      power_mpp:   ['Pmax@STC', 'Pmax@STC [W]'],
      fill_factor: ['FF@STC'],
      irradiance:  ['Irradiance front', 'Irradiance [W/m'],
      module_temp: ['Temperature [°C]', 'Temperature ['],
    },
    extrasLabels: {
      test_number:       ['Test number'],
      timestamp:         ['Date and time'],
      pmax_nominal:      ['Pmax@Nominal', 'Pmax@Nominal [W]'],
      voc_nominal:       ['Voc@Nominal', 'Voc@Nominal [V]'],
      vmpp_nominal:      ['Vmpp@Nominal', 'Vmpp@Nominal [V]'],
      impp_nominal:      ['Impp@Nominal', 'Impp@Nominal [A]'],
      isc_nominal:       ['Isc@Nominal', 'Isc@Nominal [A]'],
      ff_nominal:        ['FF@Nominal'],
      pmax_opc:          ['Pmax@OPC', 'Pmax@OPC [W]'],
      voc_opc:           ['Voc@OPC', 'Voc@OPC [V]'],
      vmpp_opc:          ['Vmpp@OPC', 'Vmpp@OPC [V]'],
      impp_opc:          ['Impp@OPC', 'Impp@OPC [A]'],
      isc_opc:           ['Isc@OPC', 'Isc@OPC [A]'],
      ff_opc:            ['FF@OPC'],
      degradation_pct:   ['Degradation [%]', 'Degradation'],
      pmax_tol_plus:     ['Pmax Tollerance+', 'Pmax Tolerance+'],
      pmax_tol_minus:    ['Pmax Tollerance-', 'Pmax Tolerance-'],
      module_age_years:  ['Age of service'],
      irr_back:          ['Irradiance back si', 'Irradiance back'],
      ref_cell_temp:     ['Reference cell tem', 'Reference cell temp'],
      rs_ohm:            ['Rs ['],
      alpha_pct:         ['Alpha ['],
      beta_pct:          ['Beta ['],
    },
    sessionLabels: {
      manufacturer: ['Manufacturer'],
      model:        ['Model'],
      module_type:  ['Module Type'],
      num_modules:  ['Number of modules'],
      num_strings:  ['Number of strings'],
    },
  },

  detect(rawRows, fileName) {
    const r0 = Array.isArray(rawRows[0]) ? rawRows[0] as unknown[] : [];
    if (
      String(r0[0] ?? '').trim() === 'Manufacturer' &&
      String(r0[2] ?? '').trim() === 'Number of modules'
    ) return 0.95;
    if (/iv.?[56]00|htit|htalia/i.test(fileName)) return 0.7;
    return 0;
  },

  // Only the "full" sheet contains the complete dataset — min/mid are
  // compact summaries of the same measurements with fewer columns.
  // "samples" has a completely different structure (raw V,I trace data).
  filterSheet(rawRows, sheetName) {
    if (!/^full$/i.test(sheetName.trim())) return false;
    const r0 = Array.isArray(rawRows[0]) ? rawRows[0] as unknown[] : [];
    return (
      String(r0[0] ?? '').trim() === 'Manufacturer' &&
      String(r0[2] ?? '').trim() === 'Number of modules'
    );
  },

  recommendSheet(sheetName) {
    return /^full$/i.test(sheetName.trim());
  },

  // Count by number of "Manufacturer" header rows — one per block.
  estimateCount(rawRows) {
    return rawRows.filter((row) =>
      Array.isArray(row) && String((row as unknown[])[0] ?? '').trim() === 'Manufacturer'
    ).length;
  },
};

const SEAWARD_SOLAR: IVDeviceAdapter = {
  id: 'seaward-solar',
  name: 'Solar Survey 200R/600R',
  manufacturer: 'Seaward',
  description: 'CSV/Excel export from Seaward Solar Survey handheld tester',
  icon: '☀️',
  mode: 'column',
  columnConfig: { headerRowIndex: 0, columnMapping: {} },
  detect(_rawRows, fileName) {
    if (/seaward|solar.?survey/i.test(fileName)) return 0.8;
    return 0;
  },
};

const HIOKI_PV: IVDeviceAdapter = {
  id: 'hioki-pv',
  name: 'PV Analyzer PV3360/3561',
  manufacturer: 'HIOKI',
  description: 'CSV export from HIOKI PV Analyzer handheld instruments',
  icon: '🔌',
  mode: 'column',
  columnConfig: { headerRowIndex: 0, columnMapping: {} },
  detect(rawRows, fileName) {
    if (/hioki/i.test(fileName)) return 0.85;
    for (let r = 0; r < Math.min(5, rawRows.length); r++) {
      const row = Array.isArray(rawRows[r]) ? rawRows[r] as unknown[] : [];
      if (row.some((v) => typeof v === 'string' && /hioki/i.test(v))) return 0.8;
    }
    return 0;
  },
};

const FLUKE_IRR: IVDeviceAdapter = {
  id: 'fluke-irr',
  name: 'IRR-1 Solar Irradiance + IV',
  manufacturer: 'Fluke',
  description: 'Export from Fluke solar IRR/IV combo meters',
  icon: '🔶',
  mode: 'column',
  columnConfig: { headerRowIndex: 0, columnMapping: {} },
  detect(_rawRows, fileName) {
    if (/fluke/i.test(fileName)) return 0.8;
    return 0;
  },
};

const SOLMETRIC_SUNSPEC: IVDeviceAdapter = {
  id: 'solmetric-sunspec',
  name: 'SunscannerIV',
  manufacturer: 'Solmetric',
  description: 'CSV/Excel export from Solmetric SunscannerIV',
  icon: '📡',
  mode: 'column',
  columnConfig: { headerRowIndex: 0, columnMapping: {} },
  detect(_rawRows, fileName) {
    if (/solmetric|sunspec|sunscanner/i.test(fileName)) return 0.8;
    return 0;
  },
};

export const MANUAL_COLUMN: IVDeviceAdapter = {
  id: 'manual-column',
  name: 'Manual — Standard / Transposed',
  manufacturer: 'Custom',
  description: 'Manually map columns for any CSV or Excel file',
  icon: '📋',
  mode: 'column',
  columnConfig: { headerRowIndex: 0, columnMapping: {} },
  detect() { return 0; },
};

export const MANUAL_BLOCK: IVDeviceAdapter = {
  id: 'manual-block',
  name: 'Manual — Block Template',
  manufacturer: 'Custom',
  description: 'Manually configure block layout for fixed-row-per-measurement formats',
  icon: '⊞',
  mode: 'block',
  blockConfig: { blockSize: 10, firstRow: 0, cellMapping: { label: { row: 0, col: 0 } } },
  detect() { return 0; },
};

// ─── Registry & Detection ──────────────────────────────────────────────────────

export const IV_DEVICE_ADAPTERS: IVDeviceAdapter[] = [
  HT_IV600,
  SEAWARD_SOLAR,
  HIOKI_PV,
  FLUKE_IRR,
  SOLMETRIC_SUNSPEC,
  MANUAL_COLUMN,
  MANUAL_BLOCK,
];

/**
 * Run all adapters' detect() and return the highest-confidence match.
 * Returns null if no adapter scores ≥ 0.4.
 */
export function detectAdapter(rawRows: unknown[][], fileName: string): IVDeviceAdapter | null {
  let best: IVDeviceAdapter | null = null;
  let bestScore = 0.4; // minimum threshold

  for (const adapter of IV_DEVICE_ADAPTERS) {
    const score = adapter.detect(rawRows, fileName);
    if (score > bestScore) {
      bestScore = score;
      best = adapter;
    }
  }

  return best;
}
